import React, { useRef, useState, useEffect, useMemo, MutableRefObject } from 'react';
import { observer } from 'mobx-react-lite';
import { SelectMultipleState } from './SelectMultiple.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    SelectWrapper,
    SelectElement,
    Values,
    Value,
    Options,
    Option,
    RemoveValue,
    SelectError,
} from './SelectMultiple.style';

const useClickOutside = (
    ref: MutableRefObject<unknown>,
    targetToIgnore: MutableRefObject<unknown>,
    onClickOutsideCallback: () => void
): void => {
    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (
                ref.current !== null &&
                // @ts-expect-error
                ref.current.contains(event.target) === false &&
                event.target !== targetToIgnore.current
            ) {
                onClickOutsideCallback();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return (): void => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref]);
};

export interface SelectOptionsType {
    [key: string]: string;
}

interface SelectPropsType {
    className?: string;
    label: string;
    options: SelectOptionsType;
    minSelections: number;
    maxSelections: number;
    placeholder: string;
    state: FormInputState<Array<string>, Array<string>>;
    dataTest?: string;
}

export const SelectMultiple = observer(
    ({ className, label, options, state, maxSelections, dataTest }: SelectPropsType): JSX.Element => {
        const optionsArray = useMemo(() => Object.keys(options), [options]);
        const [selectState] = useState(() => new SelectMultipleState(state, maxSelections));
        const valuesRef = useRef(null);
        const optionsRef = useRef(null);

        useClickOutside(optionsRef, valuesRef, selectState.hideOptions);

        const renderStatusMessage = (): JSX.Element | null => {
            if (selectState.isEmpty === true) {
                return <SelectError>This field is required</SelectError>;
            }
            return null;
        };

        return (
            <SelectWrapper
                className={className}
                data-test={dataTest}
            >
                <Values
                    ref={valuesRef}
                    onClick={selectState.toggleOptionsVisibility}
                    data-test={`${dataTest}-values`}
                >
                    {selectState.value.map((value) => {
                        if (options[value] !== undefined) {
                            return (
                                <Value
                                    key={value}
                                    data-test={`${dataTest}-value-${value}`}
                                >
                                    {options[value]}
                                    <RemoveValue
                                        onClick={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => {
                                            event.stopPropagation();
                                            selectState.hideOptions();
                                            selectState.removeValue(value);
                                        }}
                                        data-test={`${dataTest}-remove-value-${value}`}
                                    >
                                        &#215;
                                    </RemoveValue>
                                </Value>
                            );
                        }
                        return null;
                    })}
                </Values>
                <Options
                    ref={optionsRef}
                    hidden={!selectState.areOptionsVisible}
                    data-test={`${dataTest}-options`}
                >
                    {optionsArray.map((value: string) => {
                        //@ts-expect-error
                        const optionLabel: string = options[value];
                        return (
                            <Option
                                key={value}
                                onClick={(): void => {
                                    selectState.addValue(value);
                                }}
                                isSelected={Boolean(selectState.value.find((selectedValue) => selectedValue === value))}
                                data-test={`${dataTest}-option-${value}`}
                            >
                                {optionLabel}
                            </Option>
                        );
                    })}
                </Options>
                <SelectElement
                    name={label}
                    value={selectState.value}
                    onChange={selectState.handleChange}
                    onFocus={selectState.handleFocus}
                    onBlur={selectState.handleBlur}
                    multiple
                    hidden
                >
                    {optionsArray.map((value: string) => {
                        //@ts-expect-error
                        const optionLabel: string = options[value];
                        return (
                            <option
                                key={value}
                                value={value}
                            >
                                {optionLabel}
                            </option>
                        );
                    })}
                </SelectElement>
                {renderStatusMessage()}
            </SelectWrapper>
        );
    }
);
